import { themeTitleParts } from '../../helpers/intelligence/themeTitle'
import { STAGES } from '../../helpers/intelligence/themeReadiness'
import telwoord from '../../helpers/intelligence/telwoord'

/**
 * @typedef {object} ThemeData
 * @property {string} id - Stabiele sleutel `msi:<msiId>|tg:<doelgroep>`
 * @property {string} slug - Leesbare sleutel; mag verschuiven bij een hernoeming
 * @property {string} effectName - Naam van het effect
 * @property {string} targetgroupLabel - Leesbare naam van de doelgroep
 * @property {number} orgs - Organisaties die zich op deze doelgroep richten
 * @property {number} orgsOnEffect - Organisaties met een gecategoriseerd mechanisme op dit effect
 * @property {{resolved: number, total: number}|null} coverage - Dekking; null = niet gemeten
 * @property {number} insightCount - Inzichten onder dit thema
 * @property {number} publishedCount - Daarvan gepubliceerd
 * @property {{orgs: number, count: number}} [interviews] - Eigen, gereviewde interviews; ontbreekt bij een oudere functie
 */

/**
 * Wat er onder dit thema hangt, als zin. Drie toestanden die uit elkaar moeten
 * blijven: nog geen inzichten, wel inzichten maar niets buiten, en buiten.
 *
 * @param {number} totaal @param {number} buiten @returns {string}
 */
const stand = (totaal, buiten) => {
    if (!totaal) return 'Nog geen inzichten'
    if (!buiten) return `${telwoord(totaal, 'inzicht', 'inzichten')}, nog niets gepubliceerd`
    return `${telwoord(buiten, 'inzicht', 'inzichten')} gepubliceerd`
}

/** @param {number} stage @returns {string} */
const tredeKlasse = (stage) => {
    if (stage === STAGES.published) return ' intelligence-badge--published'
    if (stage === STAGES.publishable) return ' intelligence-badge--ready'
    return ' intelligence-badge--draft'
}

/**
 * Eén thema in de lijst: doelgroep × effect, de trede van publiceerbaarheid en
 * wat eronder hangt. De hele rij is één knop naar het themadetail.
 *
 * Alleen spans erin: een `<p>` of kop binnen een knop is ongeldige HTML en
 * verliest zijn rol.
 * AI-CONTEXT: See docs/pitfalls.md#ui-heading-inside-button-stops-being-a-heading
 *
 * De cijfers (organisaties, dekkingsgraad) staan hier bewust NIET: die horen op
 * het detail, waar ze de onderbouwing zijn. Hier gaat het om wat er nog moet
 * gebeuren.
 *
 * @param {object} props
 * @param {ThemeData} props.theme - Het thema
 * @param {{stage: number, label: string, needsReview: boolean}} props.readiness - Trede uit themeReadiness
 * @param {(id: string) => void} props.onOpen - Aangeroepen met de thema-sleutel
 */
const ThemeRow = ({ theme, readiness, onOpen }) => {
    // Zelfde helper als de detailpagina: één thema heet overal hetzelfde.
    const { effect, targetgroup } = themeTitleParts(theme)
    return (
        <button type="button" className="intelligence-theme-row" onClick={() => onOpen(theme.id)}>
            <span className="intelligence-theme-row__title">
                {effect}
                {targetgroup && <span className="intelligence-theme-row__targetgroup"> voor {targetgroup}</span>}
            </span>
            <span className="intelligence-theme-row__meta">
                <span className={`intelligence-badge${tredeKlasse(readiness.stage)}`}>{readiness.label}</span>
                {readiness.needsReview && <span className="intelligence-badge intelligence-badge--review">Nalopen</span>}
                {/* Kwalitatief staat NAAST de trede en verandert hem niet (besluit
                    user 2026-09-14). Alleen als de telling bekend is: een
                    ontbrekend veld is 'onbekend', geen nul. */}
                {theme.interviews && theme.interviews.orgs > 0 && (
                    <span className="intelligence-badge intelligence-badge--sector">Kwalitatief beschikbaar</span>
                )}
                <span className="intelligence-theme-row__stand">{stand(theme.insightCount, theme.publishedCount)}</span>
            </span>
        </button>
    )
}

export default ThemeRow
